import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { getKibbleCurrent, markTipOpened, parseTipSteps, type KibbleTip } from "~/api/kibble"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { PageShell } from "~/components/PageShell"
import { assetUrl } from "~/utils/assetUrl"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { ChevronDown, PawPrint } from "lucide-react"

export function TipsPage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [tip, setTip] = useState<KibbleTip | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([getProfile(), getKibbleCurrent()])
      .then(([profileData, kibbleData]) => {
        setProfile(profileData)
        setTip(kibbleData.tip)
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    if (!tip || tip.opened_at) return
    void markTipOpened(tip.delivery_id).catch(() => {})
  }, [tip])

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!profile) return null

  const dog = profile.dog_profile
  const dogName = dog?.name ?? "Your dog"
  const archetypeName = dog?.archetype_label ?? "Archetype"
  const heroImage = heroPortraitUrl(dog?.archetype_icon, dog?.portrait)
    ?? (dog?.archetype_icon ? assetUrl(`/images/archetypes/portraits/${dog.archetype_icon}`) : null)

  const steps = tip ? parseTipSteps(tip.how_to_do_it) : []

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="flex justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
          >
            <PawPrint size={14} />
            {dogName}
            <ChevronDown size={14} />
          </button>
        </div>

        {heroImage && (
          <HeroPortraitFrame
            className="mt-4"
            src={heroImage}
            alt={`${archetypeName} tip hero`}
          />
        )}

        {!tip ? (
          <section className="mt-6 text-center">
            <h1 className="text-2xl font-medium text-[var(--color-text-primary)]">No tip yet</h1>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              Your first tip will appear here after your next scheduled delivery.
            </p>
          </section>
        ) : (
          <>
            <section className="mt-4 text-center">
              <h1 className="text-3xl font-medium leading-tight text-[var(--color-text-primary)]">{tip.title}</h1>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">Updated this week</p>
              {tip.category && (
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
                  {tip.category}
                </p>
              )}
            </section>

            <article className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-5">
              {tip.why_it_matters && (
                <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{tip.why_it_matters}</p>
              )}

              {steps.length > 0 && (
                <>
                  <h2 className="mt-4 text-xl font-semibold text-[var(--color-text-primary)]">How to do it</h2>
                  <div className="mt-3 space-y-3">
                    {steps.map((step) => (
                      <section
                        key={step}
                        className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-3"
                      >
                        <div className="flex items-start gap-2">
                          <PawPrint size={14} className="mt-0.5 shrink-0 text-[var(--color-text-secondary)]" />
                          <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">{step}</p>
                        </div>
                      </section>
                    ))}
                  </div>
                </>
              )}
            </article>
          </>
        )}

        <div className="mt-5 text-center">
          <Link
            to="/dashboard"
            className="inline-flex rounded-full border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-6 py-2 text-sm font-medium text-[var(--color-text-primary)]"
          >
            Back to Dashboard
          </Link>
        </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
